import React, { startTransition, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Grid,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useSnackbar } from 'src/view/components/SnackbarService';
import clientService, { clearAuthTokens } from 'src/clientService';
import { HTTPValidationError } from 'src/api/Api';
import { useAppState } from 'src/state-management/state-provider';
import { getPasswordPolicyError, getPasswordStrength, PASSWORD_POLICY_TEXT } from 'src/util/password-policy';

const ChangePasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const { dispatch } = useAppState();
    const { showSnackbar, snackbarType } = useSnackbar();

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const passwordStrength = getPasswordStrength(passwords.newPassword);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPasswords((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear errors on change
        if (errors[name as keyof typeof errors]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = (): boolean => {
        let isValid = true;
        const newErrors = { currentPassword: '', newPassword: '', confirmPassword: '' };

        if (!passwords.currentPassword.trim()) {
            newErrors.currentPassword = 'Current Password is required.';
            isValid = false;
        }

        if (!passwords.newPassword.trim()) {
            newErrors.newPassword = 'New Password is required.';
            isValid = false;
        } else {
            const policyError = getPasswordPolicyError(passwords.newPassword);
            if (policyError) {
                newErrors.newPassword = policyError;
                isValid = false;
            }
        }

        if (passwords.currentPassword && passwords.currentPassword === passwords.newPassword) {
            newErrors.newPassword = 'New password must be different from the current password.';
            isValid = false;
        }

        if (!passwords.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Confirm Password is required.';
            isValid = false;
        } else if (passwords.newPassword !== passwords.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match.';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        const payload: any = {
            current_password: passwords.currentPassword,
            password: passwords.newPassword,
        };

        clientService.api
            .updateUserApiV1AuthUserPut(payload)
            .then(() => {
                showSnackbar({ message: 'Password changed successfully. Please log in again.', type: snackbarType.SUCCESS });
                setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
                clearAuthTokens();
                startTransition(() => {
                    dispatch({ type: 'LOGOUT' });
                    navigate('/auth');
                });
            })
            .catch((error: HTTPValidationError | any) => {
                const errorMessage = error?.error?.detail || error?.detail?.[0]?.msg || error?.message || 'Error changing password.';
                showSnackbar({
                    message: `Error: ${errorMessage}`,
                    type: snackbarType.ERROR,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <Container maxWidth="sm">
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 4 }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <IconButton onClick={() => startTransition(() => navigate(-1))} color="primary" sx={{ mr: 1 }} aria-label="Back" disabled={isSubmitting}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5" component="h1">Change Password</Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            type="password"
                            label="Current Password"
                            name="currentPassword"
                            value={passwords.currentPassword}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            error={!!errors.currentPassword}
                            helperText={errors.currentPassword}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            type="password"
                            label="New Password"
                            name="newPassword"
                            value={passwords.newPassword}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            error={!!errors.newPassword}
                            helperText={errors.newPassword || PASSWORD_POLICY_TEXT}
                        />
                        {passwords.newPassword && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
                                    {[1, 2, 3, 4].map(step => (
                                        <Box
                                            key={step}
                                            sx={{
                                                height: 4,
                                                borderRadius: 2,
                                                bgcolor: step <= passwordStrength.score ? 'primary.main' : 'grey.300',
                                            }}
                                        />
                                    ))}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    {passwordStrength.label}
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            type="password"
                            label="Confirm Password"
                            name="confirmPassword"
                            value={passwords.confirmPassword}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                    <Button onClick={() => startTransition(() => navigate(-1))} color="inherit" disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || !passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword} startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Save />}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default ChangePasswordPage;
